import React, { useState, useEffect, useRef } from 'react';
import { InputDataPicker, DataPickerContainer, Label, DateInputIcon, IconContainer, InputContainer } from './DataPicker.styles';
import { ErrorMessage } from '../TextInput/TextInput.styled';
import { formatInput, monthsBetweenDateAndToday } from '@/utils/handleDate';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import _ from 'lodash';
import { isAtLeast18YearsOld } from '@/utils/isAtLeast18YearsOld';

export interface DatePickerProps {
  dataTestId?: string;
  propertyName: string;
  value?: string;
  placeholder?: string;
  label?: string;
  onChange?: (option: string, propertyType: string) => void;
  propertyType: string;
  isSoftPull?: boolean;
  typeOfIcon?: string;
  iconColor?: string;
  size?: string;
  isOccupancyDate?: boolean;
  labelContainer?: boolean;
  hasAsterisk?: boolean;
  isRequired?: boolean;
  setIsErrorForm?: React.Dispatch<React.SetStateAction<boolean>>;
  isHidden: boolean;
  setIsValidDateOfBirth?: React.Dispatch<React.SetStateAction<boolean>>;
  placeholderColor?: string;
  isSentitiveInfo?: boolean;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  dataTestId,
  propertyType,
  propertyName,
  value = '',
  placeholder = '',
  onChange,
  typeOfIcon,
  iconColor = '#5140E9',
  size = '18rem',
  labelContainer = false,
  label = '',
  hasAsterisk = false,
  isRequired = true,
  setIsErrorForm,
  isHidden,
  setIsValidDateOfBirth,
  placeholderColor = '#aaafb7',
  isSentitiveInfo = false,
}) => {
  const currentForm = useAppSelector((state) => state.CurrentForm);
  const templateConfig = useAppSelector((state) => state.TemplateConfig);
  const isUserInputRef = useRef(false);
  const inputTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [formattedValue, setFormattedValue] = useState('');
  const [validationDateMessage, setValidationDateMessage] = useState('');
  const [invalidDate, setInvalidDate] = useState(false);
  const [maxLength, setMaxLength] = useState<number | undefined>(undefined);
  const isOccupancyDate = propertyName === 'propertyOccupancyDate';
  const dispatch = useAppDispatch();
  let loanApplication = useAppSelector((state) => state.LoanApplication);
  const isSoftPullDate = (propertyName: string): boolean => {
    return propertyName !== 'propertyOccupancyDate' && propertyName !== 'dateOfBirth';
  };
  const isSoftPull = isSoftPullDate(propertyName);
  const shouldApplyMasking = isSentitiveInfo && !templateConfig?.pages?.features?.newRelic?.unmaskSensitiveFields;
  useEffect(() => {
    setMaxLength(propertyName === 'propertyOccupancyDate' ? 7 : 10);
  }, [propertyName]);

  useEffect(() => {
    if (isUserInputRef.current) {
      return;
    }

    const debouncedFormat = _.debounce((input: string) => {
      const cleanedInput = input.replace(/[^0-9/]/g, '');
      const formatted = formatInput(cleanedInput, propertyName);
      setFormattedValue(formatted || '');
    }, 50);

    debouncedFormat(value);

    return () => {
      debouncedFormat.cancel();
    };
  }, [value, propertyName]);

  useEffect(() => {
    if (isUserInputRef.current) {
      return;
    }

    if (isSoftPull === true && value && value !== '') {
      const formatted = formatInput(value, propertyName);
      const validationResult = validateDate(formatted, propertyName);

      setValidationDateMessage(validationResult.message);
      setInvalidDate(!validationResult.isValid);

      if (setIsValidDateOfBirth) {
        setIsValidDateOfBirth(validationResult.isValid);
      }
      if (setIsErrorForm) {
        setIsErrorForm(!validationResult.isValid);
      }
    }
  }, [value, propertyName]);

  const validateDate = (formatted: string, propertyName: string) => {
    if (propertyName === 'propertyOccupancyDate') {
      const validFormat = /^(0[1-9]|1[0-2])\/\d{4}$/.test(formatted);
      const minMonths = 6;
      const hasMinMonths = monthsBetweenDateAndToday(formatted, propertyName) >= minMonths;

      const year = parseInt(formatted.split('/')[1], 10);

      if (year < 1900) {
        return {
          isValid: false,
          message: 'Year must be 1900 or later',
        };
      }

      if (!validFormat) {
        return {
          isValid: false,
          message: 'Date must be in MM/YYYY format',
        };
      } else if (!hasMinMonths) {
        return {
          isValid: false,
          message: `This program requires at least ${minMonths} months of ownership`,
        };
      }
      return {
        isValid: true,
        message: '',
      };
    } else {
      const validFormat = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/.test(formatted);

      const hasMinMonths = isAtLeast18YearsOld(formatted);

      const year = parseInt(formatted.split('/')[2], 10);
      if (year < 1900) {
        return {
          isValid: false,
          message: 'Year must be 1900 or later',
        };
      }

      if (!validFormat) {
        return {
          isValid: false,
          message: 'Date must be in MM/DD/YYYY format',
        };
      } else if (!hasMinMonths) {
        return {
          isValid: false,
          message: 'You must be at least 18 years old to apply for a loan.',
        };
      }
      return {
        isValid: true,
        message: '',
      };
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    isUserInputRef.current = true;

    if (inputTimeoutRef.current) clearTimeout(inputTimeoutRef.current);

    inputTimeoutRef.current = setTimeout(() => {
      isUserInputRef.current = false;
    }, 1000);

    let inputValue = event.target.value.replace(/[^0-9/]/g, '');
    let formatted = formatInput(inputValue, propertyName);
    setFormattedValue(formatted);
    if (onChange) onChange(formatted, propertyType);
    const validationResult = validateDate(formatted, propertyName);

    if (propertyName === 'dateOfBirth' || propertyName === 'birthData' || propertyName === 'dateOfBirthCoBorrower') {
      updateData(formatted);
    }

    setValidationDateMessage(validationResult.message);
    setInvalidDate(!validationResult.isValid);

    if (setIsValidDateOfBirth) {
      setIsValidDateOfBirth(validationResult.isValid);
    }
    if (setIsErrorForm) {
      setIsErrorForm(!validationResult.isValid);
    }
  };

  const updateData = (value: string) => {
    const updatedBorrowers = loanApplication.borrowers.map((borrower, index) => {
      if ((propertyName === 'dateOfBirth' || propertyName === 'birthData') && index === 0) {
        return { ...borrower, borrowerDOB: value };
      }
      if (propertyName === 'dateOfBirthCoBorrower' && index === 1) {
        return { ...borrower, borrowerDOB: value };
      }
      return borrower;
    });

    const auxLoanApplication = {
      ...loanApplication,
      borrowers: updatedBorrowers,
    };

    dispatch({
      type: 'LoanApplication/updateValue',
      payload: auxLoanApplication,
    });
  };

  const onBlur = () => {
    if (!formattedValue && isRequired) {
      setValidationDateMessage('Date is required');
      setInvalidDate(true);
      if (setIsErrorForm) setIsErrorForm(true);
      if (setIsValidDateOfBirth) setIsValidDateOfBirth(false);
    } else if (!formattedValue && !isRequired) {
      setValidationDateMessage('');
      setInvalidDate(false);
      if (setIsErrorForm) setIsErrorForm(false);
      if (setIsValidDateOfBirth) setIsValidDateOfBirth(true);
    } else if (formattedValue) {
      const validationResult = validateDate(formattedValue, propertyName);
      setValidationDateMessage(validationResult.message);
      setInvalidDate(!validationResult.isValid);

      if (setIsErrorForm) setIsErrorForm(!validationResult.isValid);
      if (setIsValidDateOfBirth) setIsValidDateOfBirth(validationResult.isValid);

      if (propertyName === 'propertyOccupancyDate') {
        const validFormat = /^(0[1-9]|1[0-2])\/\d{4}$/.test(formattedValue);
        if (!validFormat) {
          setValidationDateMessage('Date must be in MM/YYYY format');
          setInvalidDate(true);
          if (setIsErrorForm) setIsErrorForm(true);
        }
      } else {
        const validFormat = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/.test(formattedValue);
        if (!validFormat) {
          setValidationDateMessage('Date must be in MM/DD/YYYY format');
          setInvalidDate(true);
          if (setIsErrorForm) setIsErrorForm(true);
          if (setIsValidDateOfBirth) setIsValidDateOfBirth(false);
        }
      }
    }
  };

  useEffect(() => {
    if (setIsErrorForm) {
      setIsErrorForm(invalidDate);
    }
  }, [invalidDate, setIsErrorForm]);

  useEffect(() => {
    if (currentForm[propertyType] && !isUserInputRef.current) {
      const formatted = formatInput(currentForm[propertyType], propertyName);
      setFormattedValue(formatted);
    }
  }, [propertyType, currentForm]);

  const isPlaceholder: boolean = !formattedValue;

  return (
    <DataPickerContainer $hasAsterisk={hasAsterisk} $isHidden={isHidden}>
      {labelContainer && <Label $hasAsterisk={hasAsterisk}>{label}</Label>}
      <InputContainer $isError={invalidDate} $isOccupancyDate={isOccupancyDate}>
        {typeOfIcon && (
          <IconContainer>
            <DateInputIcon $iconColor={iconColor} />
          </IconContainer>
        )}
        <InputDataPicker
          data-testid={dataTestId}
          placeholder={placeholder}
          onFocus={() => setInvalidDate(false)}
          onBlur={onBlur}
          onChange={handleChange}
          value={formattedValue}
          $isSoftPull={isSoftPull}
          size={size}
          maxLength={maxLength}
          $isPlaceholder={isPlaceholder}
          $placeholderColor={placeholderColor}
          {...(shouldApplyMasking && { className: 'nr-block' })}
        />
      </InputContainer>

      {invalidDate && <ErrorMessage>{validationDateMessage}</ErrorMessage>}
    </DataPickerContainer>
  );
};

export default DatePicker;
